import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  getBatches, 
  getProducts, 
  createTransformation, 
  getTransformations,
  getBatchTraceability 
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Loader2, 
  Scissors, 
  ArrowRight, 
  Package, 
  Trash2,
  GitBranch,
  Search,
  Eye
} from 'lucide-react';

export default function Transformations() {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [transformations, setTransformations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [traceDialogOpen, setTraceDialogOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  
  // Form state
  const [sourceBatchId, setSourceBatchId] = useState('');
  const [sourceBatch, setSourceBatch] = useState(null);
  const [quantityConsumed, setQuantityConsumed] = useState('');
  const [outputs, setOutputs] = useState([{ product_id: '', batch_code: '', quantity: '', use_by_date: '' }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Check if navigated from Batches page with pre-selected batch
  useEffect(() => {
    if (location.state?.sourceBatch && batches.length > 0) {
      const preselectedBatch = location.state.sourceBatch;
      handleSourceBatchChange(preselectedBatch.id);
      setCreateDialogOpen(true);
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, batches]);

  const loadData = async () => {
    try {
      const [transRes, batchesRes, productsRes] = await Promise.allSettled([
        getTransformations(),
        getBatches({ available_only: true }),
        getProducts({ is_active: true })
      ]);
      if (transRes.status === 'fulfilled') setTransformations(transRes.value.data || []);
      if (batchesRes.status === 'fulfilled') setBatches(batchesRes.value.data || []);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSourceBatchId('');
    setSourceBatch(null);
    setQuantityConsumed('');
    setOutputs([{ product_id: '', batch_code: '', quantity: '', use_by_date: '' }]);
    setNotes('');
    setCreateDialogOpen(true);
  };

  const handleSourceBatchChange = (batchId) => {
    setSourceBatchId(batchId);
    const batch = batches.find(b => b.id === batchId);
    setSourceBatch(batch);
    if (batch) {
      // Pre-fill use_by_date for outputs
      setOutputs(outputs.map(o => ({ ...o, use_by_date: batch.use_by_date || '' })));
    }
  };

  const addOutput = () => {
    setOutputs([...outputs, { 
      product_id: '', 
      batch_code: '', 
      quantity: '', 
      use_by_date: sourceBatch?.use_by_date || '' 
    }]);
  };

  const removeOutput = (index) => {
    if (outputs.length > 1) {
      setOutputs(outputs.filter((_, i) => i !== index));
    }
  };

  const updateOutput = (index, field, value) => {
    const updated = [...outputs];
    updated[index] = { ...updated[index], [field]: value };
    setOutputs(updated);
  };

  const handleCreateTransformation = async () => {
    if (!sourceBatchId || !quantityConsumed) {
      toast.error('Please select a source batch and quantity to consume');
      return;
    }

    const validOutputs = outputs.filter(o => o.product_id && o.batch_code && o.quantity);
    if (validOutputs.length === 0) {
      toast.error('Please add at least one output product');
      return;
    }

    setSaving(true);
    try {
      const data = {
        source_batch_id: sourceBatchId,
        quantity_consumed: parseFloat(quantityConsumed),
        outputs: validOutputs.map(o => ({
          product_id: o.product_id,
          batch_code: o.batch_code,
          quantity: parseFloat(o.quantity),
          use_by_date: o.use_by_date || null
        })),
        notes: notes || null
      };

      await createTransformation(data);
      toast.success('Transformation created successfully');
      setCreateDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transformation');
    } finally {
      setSaving(false);
    }
  };

  const viewTraceability = async (batchId) => {
    setTraceLoading(true);
    setTraceDialogOpen(true);
    try {
      const response = await getBatchTraceability(batchId);
      setTraceData(response.data);
    } catch (error) {
      toast.error('Failed to load traceability data');
      setTraceDialogOpen(false);
    } finally {
      setTraceLoading(false);
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transformations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Product Transformations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cut, portion, or repack products while maintaining full batch traceability
          </p>
        </div>
        {canEdit(user?.role) && (
          <Button onClick={openCreateDialog} data-testid="btn-new-transformation">
            <Scissors className="w-4 h-4 mr-2" />
            New Transformation
          </Button>
        )}
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <GitBranch className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">How Transformations Work</p>
              <p className="text-blue-700 mt-1">
                Use transformations to convert master products (e.g., 25kg cheese wheel) into child products 
                (e.g., 100 × 250g portions). The system maintains full traceability - child batches can always 
                be traced back to their source batch for food safety recalls.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transformations List */}
      {transformations.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Source Batch</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Qty Consumed</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Outputs</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transformations.map((trans) => (
                    <tr key={trans.id} className="hover:bg-muted/30" data-testid={`transformation-row-${trans.id}`}>
                      <td className="px-4 py-3 text-sm">{trans.transformation_date}</td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-sm">{trans.source_batch_code}</div>
                        <div className="text-xs text-muted-foreground">{getProductName(trans.source_product_id)}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm">{trans.quantity_consumed}</td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {trans.outputs?.map((out, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              <span className="font-mono">{out.batch_code}</span>
                              <span className="text-muted-foreground">({out.quantity}x {out.product_name})</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                        {trans.notes || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => viewTraceability(trans.source_batch_id)}
                          className="h-8 w-8 p-0"
                          title="View traceability"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Scissors className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No transformations yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first product transformation to split or portion products
            </p>
            {canEdit(user?.role) && (
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create Transformation
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Transformation Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5" />
              Create Product Transformation
            </DialogTitle>
            <DialogDescription>
              Convert a source batch into one or more output products with full traceability
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Source Batch Selection */}
            <Card className="bg-muted/30">
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Source (What you’re cutting/splitting)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Source Batch *</Label>
                    <Select value={sourceBatchId} onValueChange={handleSourceBatchChange}>
                      <SelectTrigger data-testid="select-source-batch">
                        <SelectValue placeholder="Select batch..." />
                      </SelectTrigger>
                      <SelectContent>
                        {batches.map(b => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.batch_code} - {b.product_name} (Avail: {b.quantity_available?.toFixed(0)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity to Consume *</Label>
                    <Input
                      type="number"
                      value={quantityConsumed}
                      onChange={(e) => setQuantityConsumed(e.target.value)}
                      placeholder="e.g., 1"
                      className="font-mono"
                      data-testid="input-qty-consumed"
                    />
                    {sourceBatch && (
                      <p className="text-xs text-muted-foreground">
                        Available: {sourceBatch.quantity_available?.toFixed(2)} {sourceBatch.product_format || 'units'}
                      </p>
                    )}
                  </div>
                </div>
                
                {sourceBatch && (
                  <div className="p-3 bg-background rounded-md text-sm">
                    <p><strong>Product:</strong> {sourceBatch.product_name}</p>
                    <p><strong>Use By:</strong> {sourceBatch.use_by_date}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Output Products */}
            <Card>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Outputs (What you’re creating)</CardTitle>
                <Button size="sm" variant="outline" onClick={addOutput}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Output
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {outputs.map((output, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-muted/30 rounded-md" data-testid={`output-row-${index}`}>
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Product *</Label>
                      <Select value={output.product_id} onValueChange={(v) => updateOutput(index, 'product_id', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Batch Code *</Label>
                      <Input
                        value={output.batch_code}
                        onChange={(e) => updateOutput(index, 'batch_code', e.target.value)}
                        placeholder="e.g., CB-P-001"
                        className="font-mono"
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Quantity *</Label>
                      <Input
                        type="number"
                        value={output.quantity}
                        onChange={(e) => updateOutput(index, 'quantity', e.target.value)}
                        placeholder="100"
                        className="font-mono"
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Use By</Label>
                      <Input
                        value={output.use_by_date}
                        onChange={(e) => updateOutput(index, 'use_by_date', e.target.value)}
                        placeholder="DD/MM/YYYY"
                        className="font-mono text-xs"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeOutput(index)}
                        disabled={outputs.length === 1}
                        className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Cut into retail portions for Christmas stock"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTransformation} disabled={saving} data-testid="btn-create-transformation">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />}
              Create Transformation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Traceability Dialog */}
      <Dialog open={traceDialogOpen} onOpenChange={setTraceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Batch Traceability
            </DialogTitle>
            <DialogDescription>
              Full upstream and downstream traceability chain
            </DialogDescription>
          </DialogHeader>
          
          {traceLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : traceData ? (
            <div className="space-y-6 py-4">
              {/* Current Batch */}
              <Card className="border-primary">
                <CardHeader className="py-3 bg-primary/5">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Selected Batch
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Batch:</span> <strong className="font-mono">{traceData.batch.batch_code}</strong></div>
                    <div><span className="text-muted-foreground">Product:</span> {traceData.batch.product_name}</div>
                    <div><span className="text-muted-foreground">Use By:</span> {traceData.batch.use_by_date}</div>
                    <div><span className="text-muted-foreground">Available:</span> {traceData.batch.quantity_available?.toFixed(2)}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Source Chain (Upstream) */}
              {traceData.source_chain?.length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-amber-700">⬆️ Source Batches (Upstream)</CardTitle>
                    <CardDescription className="text-xs">Where this batch originated from</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {traceData.source_chain.map((source, i) => (
                      <div key={i} className="p-2 bg-amber-50 rounded-md text-sm flex justify-between items-center">
                        <div>
                          <span className="font-mono font-medium">{source.batch_code}</span>
                          <span className="text-muted-foreground ml-2">({source.product_name})</span>
                        </div>
                        <span className="text-xs text-muted-foreground">Use by: {source.use_by_date}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Derived Batches (Downstream) */}
              {traceData.derived_batches?.length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-emerald-700">⬇️ Derived Batches (Downstream)</CardTitle>
                    <CardDescription className="text-xs">Batches created from this one</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {traceData.derived_batches.map((derived, i) => (
                      <div key={i} className="p-2 bg-emerald-50 rounded-md text-sm flex justify-between items-center">
                        <div>
                          <span className="font-mono font-medium">{derived.batch_code}</span>
                          <span className="text-muted-foreground ml-2">({derived.product_name})</span>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          <div>Qty: {derived.quantity_produced?.toFixed(0)}</div>
                          <div>Use by: {derived.use_by_date}</div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* No relations */}
              {(!traceData.source_chain || traceData.source_chain.length === 0) && 
               (!traceData.derived_batches || traceData.derived_batches.length === 0) && (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  <p>This batch has no transformation history.</p>
                  <p>It was created directly (not derived from another batch).</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
